import React, {useState, useEffect, useMemo, useRef} from 'react';
import './App.css';
import EMDRControls from './components/EMDRControls';
import EMDRVisual from './components/EMDRVisual';
import Login from './components/Login';
import FeedbackForm from './components/FeedbackForm';

const isDesktopApp = typeof navigator !== 'undefined'
  && /Electron/i.test(navigator.userAgent || '');
const patientOnly = process.env.REACT_APP_PATIENT_ONLY === 'true';
const isPatientView = patientOnly || (typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('view') === 'patient');
const PRODUCTION_WS_URL = 'wss://api.pspenafielphd.com/ws';
const PRODUCTION_PATIENT_BASE_URL = 'https://www.pspenafielphd.com/paciente/';
const wsUrl = process.env.REACT_APP_WS_URL || PRODUCTION_WS_URL;
const patientBaseUrl = process.env.REACT_APP_PATIENT_BASE_URL
  || process.env.REACT_APP_PATIENT_URL
  || PRODUCTION_PATIENT_BASE_URL;

const STORAGE_CONFIGS = 'emdr_saved_configs';
const STORAGE_PATIENTS = 'emdr_saved_patients';

const generateId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(isDesktopApp);
  const [showFeedback, setShowFeedback] = useState(false);
  const [language, setLanguage] = useState('es');
  const [emdrActive, setEmdrActive] = useState(false);
  const [emdrSpeed, setEmdrSpeed] = useState(50);
  const [emdrPattern, setEmdrPattern] = useState('horizontal');
  const [emdrSize, setEmdrSize] = useState(80); // Tamaño de la esfera en píxeles
  const [emdrRepetitions, setEmdrRepetitions] = useState(24); // Número de repeticiones por defecto, null = infinito
  const [soundEnabled, setSoundEnabled] = useState(false); // Control de sonido (bip)
  const [soundTone, setSoundTone] = useState(800); // Frecuencia del tono en Hz (400-2000)
  const [soundVolume, setSoundVolume] = useState(30); // Volumen en porcentaje (0-100)
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [configName, setConfigName] = useState('');
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [patients, setPatients] = useState([]);
  const [patientForm, setPatientForm] = useState({
    name: '',
    description: '',
    age: '',
    diagnosis: '',
    sessionNumber: '',
    configId: '',
  });
  const [remoteSettings, setRemoteSettings] = useState(null);
  const [remoteActive, setRemoteActive] = useState(false);
  const [roomId, setRoomId] = useState('');
  const [wsStatus, setWsStatus] = useState('disconnected');
  const previewRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const wsRef = useRef(null);
  const copyTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const [copyStatus, setCopyStatus] = useState('');

  const patientUrl = useMemo(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    const isHttp = window.location.protocol === 'http:' || window.location.protocol === 'https:';
    const fallbackBase = isHttp ? window.location.origin : PRODUCTION_PATIENT_BASE_URL;
    const baseUrl = patientBaseUrl || fallbackBase;
    if (!baseUrl) {
      return '';
    }
    const url = new URL(baseUrl);
    if (!patientOnly) {
      url.searchParams.set('view', 'patient');
    }
    if (roomId) {
      url.searchParams.set('room', roomId);
    }
    return url.toString();
  }, [roomId, patientBaseUrl, patientOnly]);

  // Mantener idioma guardado (no auto-login)
  useEffect(() => {
    const savedLanguage = localStorage.getItem('emdr_language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    }
  }, []);

  useEffect(() => {
    try {
      const storedConfigs = JSON.parse(localStorage.getItem(STORAGE_CONFIGS) || '[]');
      if (Array.isArray(storedConfigs)) {
        setSavedConfigs(storedConfigs);
      }
    } catch (error) {
      console.warn('No se pudieron cargar configuraciones guardadas.');
    }

    try {
      const storedPatients = JSON.parse(localStorage.getItem(STORAGE_PATIENTS) || '[]');
      if (Array.isArray(storedPatients)) {
        setPatients(storedPatients);
      }
    } catch (error) {
      console.warn('No se pudieron cargar pacientes guardados.');
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_CONFIGS, JSON.stringify(savedConfigs));
  }, [savedConfigs]);

  useEffect(() => {
    localStorage.setItem(STORAGE_PATIENTS, JSON.stringify(patients));
  }, [patients]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (isPatientView) {
      const params = new URLSearchParams(window.location.search);
      const incomingRoom = params.get('room');
      if (incomingRoom) {
        setRoomId(incomingRoom);
      }
      return;
    }

    const storedRoom = localStorage.getItem('emdr_room_id');
    if (storedRoom) {
      setRoomId(storedRoom);
      return;
    }
    const newRoom = generateId();
    localStorage.setItem('emdr_room_id', newRoom);
    setRoomId(newRoom);
  }, []);

  useEffect(() => {
    if (!isPatientView) {
      return undefined;
    }

    const handleMessage = (event) => {
      if (!event || !event.data) {
        return;
      }
      if (event.data.type === 'EMDR_SETTINGS' && event.data.settings) {
        setRemoteSettings(event.data.settings);
      }
      if (event.data.type === 'EMDR_ACTIVE') {
        setRemoteActive(Boolean(event.data.active));
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (!wsUrl || !roomId) {
      return undefined;
    }

    let isCancelled = false;
    const connect = () => {
      if (isCancelled) {
        return;
      }
      const wsTarget = new URL(wsUrl);
      wsTarget.searchParams.set('room', roomId);
      const socket = new WebSocket(wsTarget.toString());
      wsRef.current = socket;
      setWsStatus('connecting');

      socket.addEventListener('open', () => {
        if (isCancelled) {
          return;
        }
        reconnectAttemptsRef.current = 0;
        setWsStatus('connected');
        if (!isPatientView) {
          socket.send(JSON.stringify({
            type: 'state',
            roomId,
            settings: currentSettings,
            active: emdrActive,
          }));
        }
      });

      socket.addEventListener('message', (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'state' && payload.settings) {
            setRemoteSettings(payload.settings);
            setRemoteActive(Boolean(payload.active));
          }
        } catch (error) {
          console.warn('Mensaje WS inválido.');
        }
      });

      socket.addEventListener('close', () => {
        if (isCancelled) {
          return;
        }
        setWsStatus('disconnected');
        const attempt = Math.min(reconnectAttemptsRef.current + 1, 6);
        reconnectAttemptsRef.current = attempt;
        const delay = Math.min(1000 * 2 ** (attempt - 1), 10000);
        reconnectTimeoutRef.current = setTimeout(connect, delay);
      });

      socket.addEventListener('error', () => {
        if (isCancelled) {
          return;
        }
        setWsStatus('error');
      });
    };

    connect();

    return () => {
      isCancelled = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [roomId, wsUrl, isPatientView]);

  const handleLogin = ({token, username}) => {
    setIsAuthenticated(true);
    setCurrentUser(username);
    setAuthToken(token);
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    localStorage.setItem('emdr_language', newLanguage);
  };

  const handleLogout = () => {
    if (isDesktopApp) {
      return;
    }
    setIsAuthenticated(false);
    setCurrentUser(null);
    setEmdrActive(false);
    setAuthToken(null);
  };

  const currentSettings = {
    speed: emdrSpeed,
    pattern: emdrPattern,
    size: emdrSize,
    repetitions: emdrRepetitions,
    soundEnabled,
    soundTone,
    soundVolume,
    language,
  };

  const effectiveSettings = remoteSettings || currentSettings;
  const effectiveActive = isPatientView ? remoteActive : emdrActive;
  const effectiveLanguage = isPatientView
    ? (remoteSettings?.language || language)
    : language;

  useEffect(() => {
    if (!isDesktopApp || isPatientView) {
      return;
    }
    const previewWindow = previewRef.current?.contentWindow;
    if (!previewWindow) {
      return;
    }
    previewWindow.postMessage({type: 'EMDR_SETTINGS', settings: currentSettings}, '*');
  }, [currentSettings, isDesktopApp]);

  useEffect(() => {
    if (!isDesktopApp || isPatientView) {
      return;
    }
    const previewWindow = previewRef.current?.contentWindow;
    if (!previewWindow) {
      return;
    }
    previewWindow.postMessage({type: 'EMDR_ACTIVE', active: emdrActive}, '*');
  }, [emdrActive, isDesktopApp]);

  useEffect(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }
    if (isPatientView) {
      return;
    }
    wsRef.current.send(JSON.stringify({
      type: 'state',
      roomId,
      settings: currentSettings,
      active: emdrActive,
    }));
  }, [currentSettings, emdrActive, roomId, isPatientView]);

  useEffect(() => {
    if (isPatientView) {
      return undefined;
    }
    const socket = wsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      return undefined;
    }
    const interval = setInterval(() => {
      if (socket.readyState !== WebSocket.OPEN) {
        return;
      }
      socket.send(JSON.stringify({
        type: 'state',
        roomId,
        settings: currentSettings,
        active: emdrActive,
      }));
    }, 1200);
    return () => clearInterval(interval);
  }, [currentSettings, emdrActive, roomId, isPatientView, wsStatus]);

  const handleCopyLink = async () => {
    if (!patientUrl) {
      return;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(patientUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = patientUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopyStatus('Copiado');
    } catch (error) {
      setCopyStatus('Error');
    }
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setCopyStatus(''), 1600);
  };

  const applyConfig = (config) => {
    if (!config) {
      return;
    }
    const {settings} = config;
    setEmdrSpeed(settings.speed);
    setEmdrPattern(settings.pattern);
    setEmdrSize(settings.size);
    setEmdrRepetitions(settings.repetitions);
    setSoundEnabled(settings.soundEnabled);
    setSoundTone(settings.soundTone);
    setSoundVolume(settings.soundVolume);
  };

  const handleSaveConfig = () => {
    const trimmedName = configName.trim();
    if (!trimmedName) {
      return;
    }
    const newConfig = {
      id: generateId(),
      name: trimmedName,
      createdAt: new Date().toISOString(),
      settings: {...currentSettings},
    };
    setSavedConfigs((prev) => [newConfig, ...prev]);
    setSelectedConfigId(newConfig.id);
    setConfigName('');
  };

  const handleDeleteConfig = (configId) => {
    setSavedConfigs((prev) => prev.filter((item) => item.id !== configId));
    setPatients((prev) => prev.map((patient) => (
      patient.configId === configId ? {...patient, configId: ''} : patient
    )));
    if (selectedConfigId === configId) {
      setSelectedConfigId('');
    }
  };

  const handleSavePatient = () => {
    if (!patientForm.name.trim()) {
      return;
    }
    const newPatient = {
      id: generateId(),
      name: patientForm.name.trim(),
      description: patientForm.description.trim(),
      age: patientForm.age.trim(),
      diagnosis: patientForm.diagnosis.trim(),
      sessionNumber: patientForm.sessionNumber.trim(),
      configId: patientForm.configId,
      createdAt: new Date().toISOString(),
    };
    setPatients((prev) => [newPatient, ...prev]);
    setPatientForm({
      name: '',
      description: '',
      age: '',
      diagnosis: '',
      sessionNumber: '',
      configId: '',
    });
  };

  const handleDeletePatient = (patientId) => {
    setPatients((prev) => prev.filter((patient) => patient.id !== patientId));
  };

  // Si no está autenticado, mostrar login
  if (isPatientView) {
    return (
      <div className="app-container">
        <EMDRVisual
          isActive={effectiveActive}
          speed={effectiveSettings.speed}
          pattern={effectiveSettings.pattern}
          size={effectiveSettings.size}
          repetitions={effectiveSettings.repetitions}
          soundEnabled={effectiveSettings.soundEnabled}
          soundTone={effectiveSettings.soundTone}
          soundVolume={effectiveSettings.soundVolume}
          onRepetitionsComplete={() => setEmdrActive(false)}
          language={effectiveLanguage}
          hideOverlay
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Login
        onLogin={handleLogin}
        language={language}
        onLanguageChange={handleLanguageChange}
      />
    );
  }

  return (
    <div className="app-container">
      {/* Visualización EMDR - solo web (desktop usa miniatura) */}
      {!isDesktopApp && (
        <EMDRVisual
          isActive={effectiveActive}
          speed={effectiveSettings.speed}
          pattern={effectiveSettings.pattern}
          size={effectiveSettings.size}
          repetitions={effectiveSettings.repetitions}
          soundEnabled={effectiveSettings.soundEnabled}
          soundTone={effectiveSettings.soundTone}
          soundVolume={effectiveSettings.soundVolume}
          onRepetitionsComplete={() => setEmdrActive(false)}
          language={effectiveLanguage}
        />
      )}
      
      {/* Controles siempre visibles en la parte superior */}
      <div className="app-content">
        <EMDRControls
          emdrActive={emdrActive}
          setEmdrActive={setEmdrActive}
          emdrSpeed={emdrSpeed}
          setEmdrSpeed={setEmdrSpeed}
          emdrSize={emdrSize}
          setEmdrSize={setEmdrSize}
          emdrRepetitions={emdrRepetitions}
          setEmdrRepetitions={setEmdrRepetitions}
          emdrPattern={emdrPattern}
          setEmdrPattern={setEmdrPattern}
          soundEnabled={soundEnabled}
          setSoundEnabled={setSoundEnabled}
          soundTone={soundTone}
          setSoundTone={setSoundTone}
          soundVolume={soundVolume}
          setSoundVolume={setSoundVolume}
          onShowFeedback={() => setShowFeedback(true)}
          onLogout={isDesktopApp ? null : handleLogout}
          currentUser={currentUser}
          language={language}
          onLanguageChange={handleLanguageChange}
        />
      </div>

      {isDesktopApp && (
        <div className="patient-preview">
          <div className="patient-preview-header">
            <span>Sala virtual del paciente</span>
            <span className={`ws-status ws-${wsStatus}`}>{wsStatus}</span>
            <button type="button" onClick={() => window.open(patientUrl, '_blank')}>
              Abrir en navegador
            </button>
          </div>
          <div className="patient-link">
            <input type="text" readOnly value={patientUrl} />
            <button
              type="button"
              onClick={handleCopyLink}
            >
              {copyStatus ? `Copiar link · ${copyStatus}` : 'Copiar link'}
            </button>
          </div>
          <div ref={previewCanvasRef} className="patient-preview-canvas">
            <EMDRVisual
              isActive={emdrActive}
              speed={currentSettings.speed}
              pattern={currentSettings.pattern}
              size={currentSettings.size}
              repetitions={currentSettings.repetitions}
              soundEnabled={currentSettings.soundEnabled}
              soundTone={currentSettings.soundTone}
              soundVolume={currentSettings.soundVolume}
              onRepetitionsComplete={() => setEmdrActive(false)}
              language={effectiveLanguage}
              hideOverlay
              containerRef={previewCanvasRef}
            />
          </div>
        </div>
      )}

      {isDesktopApp && (
        <div className="config-panels">
          <div className="config-panel">
            <h3>Configuraciones guardadas</h3>
            <div className="config-row">
              <input
                type="text"
                value={configName}
                onChange={(event) => setConfigName(event.target.value)}
                placeholder="Nombre de la configuración"
              />
              <button type="button" onClick={handleSaveConfig}>
                Guardar configuración
              </button>
            </div>
            <div className="config-list">
              {savedConfigs.length === 0 && (
                <p className="empty-state">No hay configuraciones guardadas.</p>
              )}
              {savedConfigs.map((config) => (
                <div key={config.id} className="config-item">
                  <div>
                    <strong>{config.name}</strong>
                    <span className="config-meta">{new Date(config.createdAt).toLocaleDateString()}</span>
                  </div>
                  <div className="config-actions">
                    <button type="button" onClick={() => applyConfig(config)}>
                      Aplicar
                    </button>
                    <button type="button" onClick={() => handleDeleteConfig(config.id)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="config-panel">
            <h3>Pacientes</h3>
            <div className="patient-form">
              <input
                type="text"
                value={patientForm.name}
                onChange={(event) => setPatientForm({...patientForm, name: event.target.value})}
                placeholder="Nombre del paciente"
              />
              <input
                type="text"
                value={patientForm.description}
                onChange={(event) => setPatientForm({...patientForm, description: event.target.value})}
                placeholder="Descripción breve"
              />
              <input
                type="text"
                value={patientForm.age}
                onChange={(event) => setPatientForm({...patientForm, age: event.target.value})}
                placeholder="Edad"
              />
              <input
                type="text"
                value={patientForm.diagnosis}
                onChange={(event) => setPatientForm({...patientForm, diagnosis: event.target.value})}
                placeholder="Diagnóstico"
              />
              <input
                type="text"
                value={patientForm.sessionNumber}
                onChange={(event) => setPatientForm({...patientForm, sessionNumber: event.target.value})}
                placeholder="Número de sesión"
              />
              <select
                value={patientForm.configId}
                onChange={(event) => setPatientForm({...patientForm, configId: event.target.value})}
              >
                <option value="">Sin configuración asignada</option>
                {savedConfigs.map((config) => (
                  <option key={config.id} value={config.id}>
                    {config.name}
                  </option>
                ))}
              </select>
              <button type="button" onClick={handleSavePatient}>
                Guardar paciente
              </button>
            </div>

            <div className="config-list">
              {patients.length === 0 && (
                <p className="empty-state">No hay pacientes guardados.</p>
              )}
              {patients.map((patient) => {
                const patientConfig = savedConfigs.find((config) => config.id === patient.configId);
                return (
                  <div key={patient.id} className="config-item">
                    <div>
                      <strong>{patient.name}</strong>
                      <span className="config-meta">
                        {patient.age ? `Edad: ${patient.age}` : 'Edad no registrada'}
                      </span>
                      <span className="config-meta">
                        {patient.sessionNumber ? `Sesión: ${patient.sessionNumber}` : 'Sesión sin número'}
                      </span>
                      {patient.diagnosis && (
                        <span className="config-meta">Diagnóstico: {patient.diagnosis}</span>
                      )}
                      {patient.description && (
                        <span className="config-meta">Descripción: {patient.description}</span>
                      )}
                      <span className="config-meta">
                        Configuración: {patientConfig ? patientConfig.name : 'Sin asignar'}
                      </span>
                    </div>
                    <div className="config-actions">
                      {patientConfig && (
                        <button type="button" onClick={() => applyConfig(patientConfig)}>
                          Aplicar configuración
                        </button>
                      )}
                      <button type="button" onClick={() => handleDeletePatient(patient.id)}>
                        Eliminar
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Formulario de feedback */}
      {showFeedback && (
        <FeedbackForm
          onClose={() => setShowFeedback(false)}
          language={language}
        />
      )}
    </div>
  );
}

export default App;
